// src/pages/Attendance.jsx

import React, { useEffect, useState } from "react";
import { db } from "../firebase"; // Adjust path as needed
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext"; // Adjust path as needed
import {
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaHourglassStart,
  FaHourglassEnd,
  FaArrowRight,
  FaArrowLeft,
} from "react-icons/fa";
import toast from "react-hot-toast";
import { getHolidayName } from "../config/holidays";
import { notifyUser, notifyRoles } from "../utils/notifications";
import "./Attendance.css";

// --- Helper Functions ---
const getStartOfDate = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getLocalDateString = (date = new Date()) => {
  // Always use IST to derive the date string
  return new Date(date).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// IST-safe: extract year, month (0-indexed), day from any Date in IST
const getISTDateParts = (date = new Date()) => {
  const [y, m, d] = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).split('-').map(Number);
  return { year: y, month: m - 1, day: d };
};

// IST-safe: create a Date at noon IST for a given IST calendar date
// Using noon avoids day-boundary shifts regardless of browser timezone
const makeISTNoon = (year, month, day) => {
  return new Date(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00+05:30`);
};

// IST-safe: format YYYY-MM-DD string from IST year/month/day
const formatISTDateStr = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const formatTime = (timestamp) => {
  if (!timestamp) return "--:--";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }) + ' (IST)';
};

const calculateHours = (checkIn, checkOut) => {
  if (!checkIn?.toDate || !checkOut?.toDate) return 0;
  const inDate = checkIn.toDate ? checkIn.toDate() : new Date(checkIn);
  const outDate = checkOut.toDate ? checkOut.toDate() : new Date(checkOut);
  const diffMs = outDate - inDate;
  if (diffMs < 0) return 0;
  return diffMs / (1000 * 60 * 60);
};

// Format decimal hours as "Xh Ym Zs" (used for panel display)
const formatHMS = (totalHours) => {
  if (!totalHours || totalHours <= 0) return '0h 0m 0s';
  const totalSecs = Math.round(totalHours * 3600);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${h}h ${m}m ${s}s`;
};

// Format decimal hours as "Xh Ym" (used for compact calendar split labels)
const formatHM = (totalHours) => {
  if (!totalHours || totalHours <= 0) return '0h 0m';
  const totalSecs = Math.round(totalHours * 3600);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  return `${h}h ${m}m`;
};

// --- Sub-Component 1: TodaysLogPanel ---
const TodaysLogPanel = ({ todaysRecord, onDataUpdate }) => {
  const { userData } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const todayDate = getStartOfDate();

  const handleCheckIn = async () => {
    setIsSubmitting(true); const toastId = toast.loading('Checking in...');
    try {
      await addDoc(collection(db, "attendance"), { uid: userData.uid, date: getLocalDateString(), type: 'present', checkIn: new Date(), checkOut: null, totalHours: 0, status: "Pending", approvedBy: null, role: userData.role });
      toast.success('Checked in successfully!', { id: toastId }); onDataUpdate();
      // Notify managers that employee checked in (needs approval)
      notifyRoles({
        roles: userData.role === 'admin' ? ['superadmin'] : ['admin', 'superadmin'],
        type: 'attendance_checkin',
        title: 'Attendance Check-In',
        body: `${userData.name || userData.email} checked in on ${getLocalDateString()} — pending approval.`,
        link: '/dashboard/manage',
        excludeUid: userData.uid,
        senderUid: userData.uid,
        senderName: userData.name || '',
        metadata: { date: getLocalDateString() },
      });
    } catch (err) { toast.error(`Check-in failed: ${err.message}`, { id: toastId }); }
    setIsSubmitting(false);
  };

  const handleCheckOut = async () => {
    if (!todaysRecord?.id || !todaysRecord?.checkIn) return;
    setIsSubmitting(true); const toastId = toast.loading('Checking out...');
    try {
      const checkOutTime = new Date(); const totalHours = calculateHours(todaysRecord.checkIn, { toDate: () => checkOutTime });
      await updateDoc(doc(db, "attendance", todaysRecord.id), { checkOut: checkOutTime, totalHours });
      toast.success('Checked out successfully!', { id: toastId }); onDataUpdate();
    } catch (err) { toast.error(`Check-out failed: ${err.message}`, { id: toastId }); }
    setIsSubmitting(false);
  };

  return (
    <div className="attendance-panel">
      <div className="panel-header"><h4>Today's Log</h4><span>{todayDate.toLocaleDateString("en-IN", { timeZone: 'Asia/Kolkata', weekday: "long", month: "long", day: "numeric" })}</span></div>
      <div className="log-details">
        <div className="log-item"><FaHourglassStart className="log-icon in" /><div><span className="log-label">Check In</span><span className="log-time">{formatTime(todaysRecord?.checkIn)}</span></div></div>
        <div className="log-item"><FaHourglassEnd className="log-icon out" /><div><span className="log-label">Check Out</span><span className="log-time">{formatTime(todaysRecord?.checkOut)}</span></div></div>
        <div className="log-item"><FaClock className="log-icon total" /><div><span className="log-label">Total Time</span><span className="log-time">{formatHMS(todaysRecord?.totalHours)}</span></div></div>
      </div>
      {todaysRecord?.totalHours > 0 && (
        <div className="log-time-breakdown">
          {todaysRecord.totalHours >= 1 && (
            <>
              <div className="breakdown-item breakdown-lunch">
                <span className="breakdown-icon">🍽️</span>
                <div>
                  <span className="breakdown-label">Lunch Break</span>
                  <span className="breakdown-value">1h 0m 0s</span>
                </div>
              </div>
              <div className="breakdown-divider" />
            </>
          )}
          <div className="breakdown-item breakdown-work">
            <span className="breakdown-icon">💼</span>
            <div>
              <span className="breakdown-label">Net Work Time</span>
              <span className="breakdown-value">{formatHMS(todaysRecord.totalHours >= 1 ? todaysRecord.totalHours - 1 : todaysRecord.totalHours)}</span>
            </div>
          </div>
        </div>
      )}
      <div className="log-actions">
        {!todaysRecord?.checkIn && <button onClick={handleCheckIn} disabled={isSubmitting} className="action-btn-primary check-in" data-tooltip="Check In for Today"><FaArrowRight /> Check In</button>}
        {todaysRecord?.checkIn && !todaysRecord?.checkOut && <button onClick={handleCheckOut} disabled={isSubmitting} className="action-btn-primary check-out" data-tooltip="Check Out for Today"><FaArrowLeft /> Check Out</button>}
        {todaysRecord?.checkOut && <p className="log-complete-msg">✅ Attendance for today is complete.</p>}
      </div>
    </div>
  );
};

// --- Sub-Component 2: CalendarGrid (Corrected Logic) ---
const CalendarGrid = ({ currentDate, records, canManageRecord, onCellClick }) => {
  // Derive year/month in IST to avoid local-timezone day shifts
  const { year, month } = getISTDateParts(currentDate);

  const renderCells = () => {
    const firstDayOfMonth = makeISTNoon(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = getLocalDateString(new Date());

    const cells = [];
    for (let i = 0; i < firstDayOfMonth; i++) cells.push(<div key={`empty-${i}`} className="day-cell empty"></div>);

    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate = makeISTNoon(year, month, day);
      const dateStr = formatISTDateStr(year, month, day);
      const record = records.get(dateStr);
      const holidayName = getHolidayName(dateStr);
      const isSunday = cellDate.getDay() === 0;
      const isWorkday = !holidayName && !isSunday;

      let cellClass = "day-cell";
      if (todayStr === dateStr) cellClass += " today";
      if (holidayName || isSunday) cellClass += " holiday";
      else if (record) cellClass += ` ${record.type || 'present'}-${record.status.toLowerCase()}`;
      
      // NEW: Pass the record itself to the check function
      const canTakeAction = canManageRecord(record);

      cells.push(
        <div key={day} className={cellClass}>
          <div className="day-number">{String(day).padStart(2, '0')}</div>
          {holidayName && <div className="holiday-name">{holidayName}</div>}
          {isSunday && !holidayName && <div className="holiday-name">Sunday</div>}
          
          {record ? (
            <>
              <div className="day-record"><FaHourglassStart className="log-icon-small" /> {formatTime(record.checkIn)}</div>
              <div className="day-record"><FaHourglassEnd className="log-icon-small" /> {formatTime(record.checkOut)}</div>
              <div className="day-record"><FaClock className="log-icon-small" /> {formatHMS(record.totalHours)}</div>
              {record.totalHours > 1 && (
                <div className="day-time-split">
                  <span className="split-lunch" title="Lunch Break">🍽 1h 0m</span>
                  <span className="split-work" title="Net Work Time">💼 {formatHM(record.totalHours - 1)}</span>
                </div>
              )}
              <div className="day-record status-record">
                <span className={`status-text status-${record.status.toLowerCase()}`}>{record.status}</span>
                {canTakeAction && <button className="cell-action-btn" onClick={() => onCellClick({ record, date: dateStr })} data-tooltip="Take Action">⚡ Action</button>}
              </div>
            </>
          ) : (
            canTakeAction && isWorkday && (
              <div className="empty-day-action">
                 <button className="cell-action-btn" onClick={() => onCellClick({ record: null, date: dateStr })} data-tooltip="Mark Attendance">⚡ Action</button>
              </div>
            )
          )}
        </div>
      );
    }
    return cells;
  };

  return (
    <div className="calendar-scroll-wrapper">
      <div className="calendar-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (<div key={d} className="day-cell header">{d}</div>))}
        {renderCells()}
      </div>
    </div>
  );
};

// --- Sub-Component 3: ActionModal ---
const ActionModal = ({ modalData, isSubmitting, onClose, onAction, onMarkAbsent, onMarkPresent }) => {
  const { record, date } = modalData;
  const handleDelete = () => { if (window.confirm("Are you sure? This action cannot be undone.")) onAction(record, 'delete'); };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" onClick={(e) => e.stopPropagation()}>
        <h4>Action for {date}</h4>
        {record ? (
          <>
            <p><strong>Status:</strong> {record.status}</p>
            {record.type === 'present' && (<><p><strong>Check In:</strong> {formatTime(record.checkIn)}</p><p><strong>Check Out:</strong> {formatTime(record.checkOut)}</p></>)}
            <div className="modal-actions">
              {record.status !== 'Approved' && <button onClick={() => onAction(record, 'approve')} disabled={isSubmitting} data-tooltip="Approve Request">✅ Approve</button>}
              {record.status !== 'Rejected' && <button onClick={() => onAction(record, 'reject')} disabled={isSubmitting} data-tooltip="Reject Request">❌ Reject</button>}
              <button onClick={handleDelete} disabled={isSubmitting} className="btn-delete" data-tooltip="Delete Request">🗑️ Delete</button>
            </div>
          </>
        ) : (
          <>
            <p>No attendance record found for this day.</p>
            <div className="modal-actions">
              <button onClick={() => onMarkPresent(date)} disabled={isSubmitting} data-tooltip="Mark as Present">✅ Mark as Present</button>
              <button onClick={() => onMarkAbsent(date)} disabled={isSubmitting} data-tooltip="Mark as Absent">🚫 Mark as Absent</button>
            </div>
          </>
        )}
        <button onClick={onClose} disabled={isSubmitting} className="modal-close-btn">Close</button>
      </div>
    </div>
  );
};

// --- Sub-Component 4: CalendarLegend ---
const CalendarLegend = () => (
    <div className="calendar-legend">
        <div className="legend-item"><span className="legend-color approved"></span> Approved</div>
        <div className="legend-item"><span className="legend-color pending"></span> Pending</div>
        <div className="legend-item"><span className="legend-color rejected"></span> Rejected</div>
        <div className="legend-item"><span className="legend-color absent"></span> Absent</div>
        <div className="legend-item"><span className="legend-color holiday"></span> Holiday/Sunday</div>
    </div>
);

// --- Main Attendance Component ---
const Attendance = ({ selectedEmployee }) => {
  const { userData } = useAuth();
  const isManagementView = !!selectedEmployee;
  const userInFocus = isManagementView ? selectedEmployee : userData;
  const targetUid = userInFocus?.uid || userInFocus?.id;
  const [todaysRecord, setTodaysRecord] = useState(null);
  const [historicalRecords, setHistoricalRecords] = useState(new Map());
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalData, setModalData] = useState(null);
  
  useEffect(() => {
    if (!targetUid) {
      setHistoricalRecords(new Map());
      setTodaysRecord(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const todayStr = getLocalDateString();
    const { year, month } = getISTDateParts(currentDate);
    const firstDayStr = formatISTDateStr(year, month, 1);
    const lastDayNum = new Date(year, month + 1, 0).getDate();
    const lastDayStr = formatISTDateStr(year, month, lastDayNum);
    const historyQuery = query(collection(db, "attendance"), where("uid", "==", targetUid));

    const unsub = onSnapshot(historyQuery, (historySnap) => {
      const rows = historySnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((item) => item.date >= firstDayStr && item.date <= lastDayStr)
        .sort((a, b) => (a.date > b.date ? -1 : 1));

      const newMap = new Map();
      let todayDoc = null;

      rows.forEach((data) => {
        newMap.set(data.date, data);
        if (data.date === todayStr) todayDoc = data;
      });

      setHistoricalRecords(newMap);
      setTodaysRecord(todayDoc);
      setLoading(false);
    }, (err) => {
      console.error("[Attendance] fetch error:", err);
      toast.error(`Error fetching attendance data: ${err.message}`);
      setLoading(false);
    });

    return () => unsub();
  }, [targetUid, currentDate]);
  
  // --- NEW PERMISSION LOGIC ---
  const canManageRecord = (record) => {
    // Superadmin can manage any record
    if (userData.role === 'superadmin') return true;
    
    // Admin can manage records IF:
    // 1. They are in the management view (viewing someone else)
    // 2. The person they are viewing is an 'employee'
    if (userData.role === 'admin' && isManagementView && userInFocus.role === 'employee') {
      return true;
    }
    
    // No one else can manage records (an admin cannot manage their own)
    return false;
  };

  const handleAction = async (record, action) => {
    setIsSubmitting(true); const toastId = toast.loading('Processing action...');
    try {
      if (action === 'delete') { await deleteDoc(doc(db, "attendance", record.id)); toast.success('Record deleted.', { id: toastId }); }
      else {
        const newStatus = action === 'approve' ? 'Approved' : 'Rejected';
        await updateDoc(doc(db, "attendance", record.id), { status: newStatus, approvedBy: userData.uid });
        toast.success(`Record has been ${newStatus.toLowerCase()}.`, { id: toastId });
        // Notify the employee about their attendance decision
        if (record.uid && record.uid !== userData.uid) {
          notifyUser({
            recipientUid: record.uid,
            type: newStatus === 'Approved' ? 'attendance_approved' : 'attendance_rejected',
            title: `Attendance ${newStatus}`,
            body: `Your attendance on ${record.date} has been ${newStatus.toLowerCase()} by ${userData.name || 'admin'}.`,
            link: '/dashboard',
            senderUid: userData.uid,
            senderName: userData.name || '',
            metadata: { date: record.date },
          });
        }
      }
      setModalData(null);
    } catch (err) { toast.error(`Action failed: ${err.message}`, { id: toastId }); }
    setIsSubmitting(false);
  };
  
  const createNewRecord = async (date, type) => {
    setIsSubmitting(true); const toastId = toast.loading(`Marking as ${type}...`);
    try {
      const newRecord = { uid: targetUid, date, type, status: type === 'absent' ? 'Approved' : 'Pending', approvedBy: type === 'absent' ? userData.uid : null, checkIn: null, checkOut: null, totalHours: 0, role: userInFocus.role };
      await addDoc(collection(db, "attendance"), newRecord);
      toast.success(`Date marked as ${type}.`, { id: toastId });
      setModalData(null);
    } catch (err) { toast.error(`Failed to mark as ${type}: ${err.message}`, { id: toastId }); }
    setIsSubmitting(false);
  };

  const changeMonth = (offset) => { setCurrentDate(prev => { const { year, month } = getISTDateParts(prev); const totalMonths = year * 12 + month + offset; const newYear = Math.floor(totalMonths / 12); const newMonth = ((totalMonths % 12) + 12) % 12; return makeISTNoon(newYear, newMonth, 15); }); };

  return (
    <div className="attendance-container">
      <h3><FaClock /> Attendance {isManagementView && `for ${userInFocus.name}`}</h3>
      {!isManagementView && <TodaysLogPanel todaysRecord={todaysRecord} onDataUpdate={() => {}} />}
      <div className="history-header"><h4>Monthly History</h4></div>
      <div className="calendar-nav"><button onClick={() => changeMonth(-1)} data-tooltip="Previous Month"><FaChevronLeft /></button><h2>{currentDate.toLocaleString("en-IN", { timeZone: 'Asia/Kolkata', month: "long", year: "numeric" })}</h2><button onClick={() => changeMonth(1)} data-tooltip="Next Month"><FaChevronRight /></button></div>
      <CalendarLegend />
      {loading ? <div className="loading-state">Loading history...</div> : <CalendarGrid currentDate={currentDate} records={historicalRecords} canManageRecord={canManageRecord} onCellClick={setModalData} />}
      {modalData && <ActionModal modalData={modalData} isSubmitting={isSubmitting} onClose={() => setModalData(null)} onAction={handleAction} onMarkAbsent={() => createNewRecord(modalData.date, 'absent')} onMarkPresent={() => createNewRecord(modalData.date, 'present')} />}
    </div>
  );
};

export default Attendance;